import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import { messageToolLabel } from "../../helpers/recentMessages";
import { RECALL_SEND, RECENT_SENDS, type RecallResult, type SentBatch } from "./helpers";

/** How the API bounds Undo — mirrored here only for the wording. */
const RECALL_WINDOW_HOURS = 24;

const relativeTime = (iso: string): string => {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

/**
 * Recent Sends — the undo button for a bulk send that shouldn't have gone out.
 *
 * Lists every bulk send of the last day and lets her delete one outright from
 * members' chats. Deliberately blunt: when a message has just gone to a hundred
 * people by mistake, the useful action is "take it all back", not a per-member
 * cleanup.
 */
const RecentSendsPanel: React.FC = () => {
  const [error, setError] = useState<string>("");
  const [result, setResult] = useState<(RecallResult & { batchId: string }) | null>(null);
  const [recallingId, setRecallingId] = useState<string>("");

  const { data, loading, refetch } = useQuery<{ chatAutomationsRecentSends: SentBatch[] }>(
    RECENT_SENDS,
    { fetchPolicy: "network-only" },
  );
  const [runRecall] = useMutation(RECALL_SEND);

  const batches = data?.chatAutomationsRecentSends ?? [];

  const handleRecall = async (batch: SentBatch): Promise<void> => {
    // Deleting a hundred members' messages is not an undoable action itself, so
    // it's worth one deliberate confirmation.
    if (
      !window.confirm(
        `Delete this message from ${batch.recallableCount} member${
          batch.recallableCount === 1 ? "'s" : "s'"
        } chat?\n\n` +
          "The message disappears from their chat completely. It cannot be undone.\n\n" +
          "Note: the push notification and email already went out when you sent it — " +
          "those can't be taken back.",
      )
    ) {
      return;
    }

    setError("");
    setResult(null);
    setRecallingId(batch.batchId);
    try {
      const { data: recalled } = await runRecall({ variables: { batchId: batch.batchId } });
      const outcome: RecallResult | null = recalled?.chatAutomationsRecallSend ?? null;
      if (outcome) {
        setResult({ ...outcome, batchId: batch.batchId });
      }
      await refetch();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong while undoing.");
    } finally {
      setRecallingId("");
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Recent sends — take one back
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2}>
        <Alert severity="info">
          Every bulk send from the last {RECALL_WINDOW_HOURS} hours. Undo deletes the message from
          members' chats completely — but the notification they got when you sent it has already
          landed, so undo tidies the chat, it can't unsend the ping.
        </Alert>

        {error && <Alert severity="error">{error}</Alert>}

        {result && (
          <Alert severity={result.failed > 0 || result.remaining > 0 ? "warning" : "success"}>
            ✅ Deleted {result.deleted} message{result.deleted === 1 ? "" : "s"}
            {result.failed > 0 &&
              ` · ❌ ${result.failed} couldn't be deleted and may still be in the member's chat`}
            {result.remaining > 0 &&
              ` · ${result.remaining} still to go — press Undo again to carry on (they're taken back a batch at a time so the request can't time out half way).`}
          </Alert>
        )}

        {loading && <CircularProgress size={20} />}

        {!loading && batches.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nothing sent in the last {RECALL_WINDOW_HOURS} hours.
          </Typography>
        )}

        {batches.length > 0 && (
          <TableContainer sx={{ maxHeight: "min(460px, 50vh)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Sent</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell align="right">Members</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((batch) => {
                  const isRecalled = Boolean(batch.recalledAt);
                  const toolLabel = messageToolLabel(batch.campaign ?? undefined);
                  return (
                    <TableRow
                      key={batch.batchId}
                      hover
                      sx={isRecalled ? { opacity: 0.55 } : undefined}
                    >
                      <TableCell>
                        <Typography variant="body2">{relativeTime(batch.sentAt)}</Typography>
                        {toolLabel && (
                          <Typography variant="caption" color="text.secondary">
                            {toolLabel}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 340,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {batch.messagePreview || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{batch.memberCount}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {isRecalled ? (
                          <Chip label="Undone" size="small" />
                        ) : (
                          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                            {recallingId === batch.batchId && <CircularProgress size={16} />}
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              disabled={batch.recallableCount === 0 || recallingId !== ""}
                              onClick={() => handleRecall(batch)}
                            >
                              ↩ Undo
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Paper>
  );
};

export default RecentSendsPanel;
